<template>
  <div class="theme-selectors">
    <div
      v-for="(themeCardId, index) of themeCardIds"
      :key="themeCardId"
      class="theme-selectors__field"
      :class="{
        'theme-selectors__field--dragging': draggedIndex === index,
        'theme-selectors__field--drop-target': dragOverIndex === index && draggedIndex !== index,
      }"
      :style="{
        'view-transition-name': getViewTransitionName(themeCardId),
        'view-transition-class': 'theme-card',
      }"
      :aria-grabbed="draggedIndex === index ? 'true' : 'false'"
      draggable="true"
      @dragstart="onDragStart($event, index)"
      @dragover.prevent="onDragOver(index)"
      @dragenter.prevent="onDragOver(index)"
      @dragleave="onDragLeave(index)"
      @drop.prevent="onDrop(index)"
      @dragend="onDragEnd()"
    >
      <span
        class="theme-selectors__drag-handle"
        aria-hidden="true"
      >
        <IconDragHandle/>
      </span>

      <Select
        :id="`theme-selector-${index}`"
        :modelValue="getModelForTheme(index)"
        :options="getThemeOptionsForIndex(index)"
        :nullable="false"
        placeholder="Kies een thema"
        :aria-label="`Thema ${index + 1}`"
        @update:modelValue="(value) => updateModelForTheme(index, value)"
      />

      <Select
        :id="`theme-color-selector-${index}`"
        :modelValue="getModelForColor(index)"
        :options="getColorOptionsForIndex(index)"
        :nullable="false"
        placeholder="Kies een kleur"
        :aria-label="`Kleur voor thema ${index + 1}`"
        @update:modelValue="(value) => updateModelForColor(index, value)"
        @open="onColorSelectorDropdownToggle($event)"
        @close="onColorSelectorDropdownToggle($event)"
      >
        <template #trigger="{ id, selectedOption, isOpen, eventListeners, dropdownId }">
          <button
            :id="id"
            v-tooltip="{
              content: 'Kies een kleur',
              ...tooltipOptionsDefaults,
            }"
            type="button"
            class="theme-selectors__color-trigger button--secondary button--compact-round"
            :class="{
              'button--active': isOpen,
            }"
            :aria-label="`Kleur voor thema ${index + 1}`"
            :aria-expanded="isOpen ? 'true' : 'false'"
            aria-haspopup="dialog"
            :aria-controls="dropdownId"
            v-bind="eventListeners"
          >
            <span
              class="theme-selectors__color-dot"
              :style="{ backgroundColor: getColorTriggerHex(selectedOption?.value) }"
            ></span>
          </button>
        </template>

        <template #option="{ option }">
          <span class="theme-selectors__color-option-content">
            <span
              class="theme-selectors__color-dot"
              :style="{ backgroundColor: getColorTriggerHex(option.value) }"
            ></span>
            <span>{{ option.label }}</span>
          </span>
        </template>
      </Select>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { nextTick, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import IconDragHandle from '@/components/svg/IconDragHandle.vue';
  import Select from '@/components/Select.vue';
  import { useViewTransition } from '@/composables/useViewTransition';
  import { useFormStore, type ColorId } from '@/stores/useFormStore';
  import type { SelectOption } from '@/components/Select.generics';
  import { tooltipOptionsDefaults } from '@/helpers/tooltipOptionsDefaults';

  const formStore = useFormStore();
  const { colorOptions, themeOptions, selectedThemeIds, selectedThemeColorIds } = storeToRefs(formStore);
  const { moveSelectedTheme, setSelectedTheme, setSelectedThemeColor, getColorHexById } = formStore;

  const themeCardIds = ref(['theme-card-1', 'theme-card-2', 'theme-card-3']);
  const draggedIndex = ref<number | null>(null);
  const dragOverIndex = ref<number | null>(null);

  // Build color options per row and disable colors already selected in other rows.
  function getColorOptionsForIndex (currentIndex: number): SelectOption<ColorId>[] {
    return colorOptions.value.map((color) => {
      const colorId = color.id as ColorId;
      const disabled = selectedThemeColorIds.value.some((selectedColorId, index) => {
        return index !== currentIndex && selectedColorId === colorId;
      });

      return {
        label: color.label,
        value: colorId,
        disabled,
      };
    });
  }

  function getThemeOptionsForIndex (currentIndex: number): SelectOption<string>[] {
    return themeOptions.value.map((theme) => {
      const disabled = selectedThemeIds.value.some((selectedThemeId, index) => {
        return index !== currentIndex && selectedThemeId === theme.id;
      });

      return {
        label: theme.label,
        value: theme.id,
        disabled,
      };
    });
  }

  function getModelForTheme (index: number): string | null {
    return selectedThemeIds.value[index] ?? null;
  }

  function updateModelForTheme (index: number, value: string | number | null) {
    setSelectedTheme(index, typeof value === 'string' ? value : null);
  }

  function getModelForColor (index: number): ColorId | null {
    return (selectedThemeColorIds.value[index] ?? null) as ColorId | null;
  }

  function updateModelForColor (index: number, value: string | number | null) {
    setSelectedThemeColor(index, typeof value === 'string' ? value as ColorId : null);
  }

  function getColorTriggerHex (colorId: ColorId | null | undefined) {
    return getColorHexById(colorId ?? null) ?? 'transparent';
  }

  function getViewTransitionName (themeCardId: string) {
    return themeCardId;
  }

  function onDragStart (event: DragEvent, index: number) {
    draggedIndex.value = index;
    dragOverIndex.value = index;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.dropEffect = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  }

  function onDragOver (index: number) {
    dragOverIndex.value = index;
  }

  function onDragLeave (index: number) {
    if (dragOverIndex.value === index) {
      dragOverIndex.value = null;
    }
  }

  function onDrop (toIndex: number) {
    if (draggedIndex.value == null || draggedIndex.value === toIndex) {
      onDragEnd();
      return;
    }

    const fromIndex = draggedIndex.value;

    onDragEnd();

    useViewTransition(async () => {
      moveThemeCardId(fromIndex, toIndex);
      moveSelectedTheme(fromIndex, toIndex);
      await nextTick();
    });
  }

  function onColorSelectorDropdownToggle (event: HTMLElement | null) {
    /*
      Dispatch a mouseleave event on the trigger element to
      hide any tooltip that might be visible on the trigger el.

      NOTE:
      I tried using `event.$_popper.hide()` here first, but it didn't
      hide the popper consistently and didn't detach the popper after hiding
      finished. Then I tried using floating-vue's `destroyTooltip()` function
      here, but it hid the popper without transitioning first.

      By dispatching a mouseleave event on the trigger element,
      we trigger the 'hover' `hideTrigger`.
    */
    event?.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
  }

  function moveThemeCardId (fromIndex: number, toIndex: number) {
    const reorderedThemeCardIds = [...themeCardIds.value];
    const movedThemeCardId = reorderedThemeCardIds.splice(fromIndex, 1)[0];

    if (movedThemeCardId == null) {
      return;
    }

    reorderedThemeCardIds.splice(toIndex, 0, movedThemeCardId);
    themeCardIds.value = reorderedThemeCardIds;
  }

  function onDragEnd () {
    draggedIndex.value = null;
    dragOverIndex.value = null;
  }
</script>

<style lang="scss">
  .theme-selectors {
    display: flex;
    flex-direction: column;
    gap: 12px;

    &__field {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto;
      align-items: center;
      gap: 10px;
      padding: 14px 16px;
      border-radius: 8px;
      background: var(--color-white);
      box-shadow: inset 0 0 0 1px color-opacity(--color-black, 7%);
      cursor: grab;
      transition:
        box-shadow var(--speed-medium) var(--easing-default),
        transform var(--speed-fast) var(--easing-default),
        opacity var(--speed-fast) var(--easing-default);

      &--dragging {
        opacity: 0.4;
        box-shadow:
          inset 0 0 0 1px color-opacity(--color-black, 7%),
          0 10px 24px color-opacity(--color-black, 14%);
        transform: translateY(-2px);
      }

      &--drop-target {
        box-shadow: inset 0 0 0 2px var(--color-primary);
        transform: translateY(-1px);
      }
    }

    &__drag-handle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 24px;
      margin-left: -2px;
      color: color-opacity(--color-black, 48%);
      pointer-events: none;
      cursor: inherit;

      svg {
        width: 18px;
        height: 18px;
      }
    }

    &__color-picker {
      position: relative;
      justify-self: end;
      z-index: 10000;
    }

    &__color-trigger {
      --button__outline-width-override: 1px;

      width: 34px;
      height: 34px;
      min-height: 0;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    &__color-dot {
      width: 18px;
      height: 18px;
      border-radius: 99999px;
      box-shadow: inset 0 0 0 1px color-opacity(--color-black, 16%);
      flex: 0 0 auto;
      transition: background-color var(--speed-instant) var(--easing-cubic-2);
    }

    &__color-option-content {
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }
  }
</style>
